import { API } from 'aws-amplify';
import moment from 'moment-timezone';
import { getAppVersion, isEmpty, only } from '../../utils/helpers';
import * as type from './types';
import { linkedVenues, restaurantById, restaurantSearchByName } from '../graphql/queries';
import { devLog } from '../../utils';
import { makeApiAction } from './actions';
import { closeEarly, reopen, saveRestaurant } from '../graphql/mutations';
import { RestaurantInputKeys } from '../models/Restaurant';

const ausTimezones = moment.tz.zonesForCountry('AU');

/**
 * Sets the timezone for all future moment instances
 * @param {string} region
 */
const setMomentTimezone = (region) => {
  // TODO: Revert back to this code once region is passed correctly and remove the temp fix below
  // const timezone = ausTimezones.find((zone) => zone.toLowerCase().match(region.toLowerCase()));

  // Temp fix for missing region
  let timezone = null;
  if (region) {
    timezone = ausTimezones.find((zone) => zone.toLowerCase().match(region.toLowerCase()));
  }

  // set the default timezone to restaurant's region
  if (timezone) {
    moment.tz.setDefault(timezone);
  } else {
    // default to the local browser timezone
    moment.tz.setDefault();
  }
};

export const fetchRestaurantAction = (restId, userId, userType) => (dispatch) => {
  dispatch({
    type: type.SET_ID_APP_LOADING,
    payload: 'RESTAURANT',
  });

  const getData = async () => {
    try {
      const response = await API.graphql(
        {
          query: restaurantById,
          variables: {
            restId,
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        },
        {
          'user-id': userId,
          'user-type': userType,
          'rest-id': restId,
          'app-version': getAppVersion(),
          'X-App-Environment': import.meta.env?.VITE_API_ENVIRONMENT,
        },
      ).catch((error) => {
        devLog('error', 'Error occurred while fetching restaurant', error);
        throw error;
      });

      devLog('success', 'restaurant', response.data.restaurantById);

      const region = response.data.restaurantById?.timezone;
      if (!isEmpty(region)) {
        setMomentTimezone(region);
      }

      const todayByTimezone = moment().weekday();

      dispatch({
        type: type.FETCH_RESTAURANT_SUCCESS,
        payload: {
          restaurant: response.data.restaurantById,
          todayByTimezone,
        },
      });
    } catch (error) {
      devLog('error', 'restaurant', error);

      dispatch({
        type: type.FETCH_RESTAURANT_FAILURE,
        payload: `Unable to retrieve Restaurant: ${error?.message || error?.errors?.[0]?.message}`,
      });
    } finally {
      dispatch({
        type: type.REMOVE_ID_APP_LOADING,
        payload: 'RESTAURANT',
      });
    }
  };
  getData();
};

export const fetchLinkedVenuesAction = (restId, userId, userType, ssoToken) => (dispatch) => {
  dispatch({
    type: type.SET_ID_APP_LOADING,
    payload: 'RESTAURANT',
  });

  const getData = async () => {
    try {
      const response = await API.graphql(
        {
          query: linkedVenues,
          variables: {
            restId,
            ...(ssoToken && { ssoToken }),
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        },
        {
          'user-id': userId,
          'user-type': userType,
          // 'rest-id': restId,
          'app-version': getAppVersion(),
          'X-App-Environment': import.meta.env?.VITE_API_ENVIRONMENT,
        },
      ).catch((error) => {
        devLog('error', 'Error occurred while fetching restaurant', error);
        throw error;
      });

      devLog('success', 'linkedVenues', response);

      dispatch({
        type: type.FETCH_LINKED_VENUES_SUCCESS,
        payload: response.data.obeeUserLinkedVenues,
      });
    } catch (error) {
      devLog('error', 'linkedVenues', error);

      dispatch({
        type: type.FETCH_LINKED_VENUES_FAILURE,
        payload: `Unable to retrieve linked venues: ${
          error?.message || error?.errors?.[0]?.message
        }`,
      });
    } finally {
      dispatch({
        type: type.REMOVE_ID_APP_LOADING,
        payload: 'RESTAURANT',
      });
    }
  };
  getData();
};

export const fetchRestaurantsByNameAction = (name, userId, userType) => (dispatch) => {
  dispatch({
    type: type.FETCH_RESTAURANTS_PENDING,
  });

  (async () => {
    try {
      const response = await API.graphql(
        {
          query: restaurantSearchByName,
          variables: {
            name,
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        },
        {
          'user-id': userId,
          'user-type': userType,
          'app-version': getAppVersion(),
          'X-App-Environment': import.meta.env?.VITE_API_ENVIRONMENT,
        },
      );

      devLog('success', 'restaurants', response.data.restaurantSearchByName);

      dispatch({
        type: type.FETCH_RESTAURANTS_SUCCESS,
        payload: response.data.restaurantSearchByName,
      });
    } catch (error) {
      devLog('error', 'restaurants', error);

      dispatch({
        type: type.FETCH_RESTAURANTS_FAILURE,
        payload: `Unable to retrieve Restaurants: ${error.errors[0].message}`,
      });
    }
  })();
};

export const clearRestaurantsByNameAction = () => (dispatch) => {
  dispatch({
    type: type.CLEAR_RESTAURANTS,
  });
};

export const saveRestaurantAction = (updatedRestaurant) => (dispatch) => {
  const action = 'SAVE_RESTAURANT';

  const updatedRestaurantData = only(updatedRestaurant, RestaurantInputKeys());

  const variables = {
    restaurant: updatedRestaurantData,
  };

  makeApiAction(
    action,
    saveRestaurant,
    variables,
    null,
    'saveRestaurant',
    true,
    dispatch,
    'Your profile has been saved',
    null,
    false,
    'Unable to save profile',
  );
};

export const closeRestaurantEarlyAction = () => (dispatch) => {
  const action = 'CLOSE_RESTAURANT';

  const variables = {};

  makeApiAction(
    action,
    closeEarly,
    variables,
    null,
    'closeEarly',
    true,
    dispatch,
    'Restaurant is now closed for the day',
    null,
    false,
    'Failed to set restaurant as closed',
  );
};

export const reopenRestaurantAction = () => (dispatch) => {
  const action = 'REOPEN_RESTAURANT';

  const variables = {};

  makeApiAction(
    action,
    reopen,
    variables,
    null,
    'reopen',
    true,
    dispatch,
    'Restaurant is now reopened for the day',
    null,
    false,
    'Failed to set restaurant as reopened',
  );
};
